import { baseApi } from '@/lib/store/api/baseQuery'
import { apiEndpoints } from '@/lib/api/endpoints'
import type { MedicationCheckInLabBundleResponse } from '@/lib/store/api/careJourneyApi'
import type { PrescriptionDetailData } from '@/types/interfaces/prescriptionDetail'

interface PrescriptionOrderLookup {
  order_id?: string
}

export interface RenewablePatientMedication {
  id: string
  name: string
  quantity?: number
  open_to_renew: boolean
  on_next_step?: boolean
}

export interface RenewablePatientMedicationsResponse {
  medications: RenewablePatientMedication[]
}

export const prescriptionApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    createPrescriptionPharmacyOrder: builder.mutation<{ pharmacy_order_id: string }, string>({
      query: (careActionPrescriptionId) => ({
        url: apiEndpoints.prescriptions.ensurePharmacyOrder(careActionPrescriptionId),
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, careActionPrescriptionId) => [
        { type: 'Prescription', id: careActionPrescriptionId },
      ],
    }),
    getPatientMedication: builder.query<PrescriptionDetailData, { id: string }>({
      query: ({ id }) => apiEndpoints.patientMedications.getById(id),
      providesTags: (_result, _error, { id }) => [
        { type: 'Prescription', id },
      ],
    }),
    getRenewablePatientMedications: builder.query<RenewablePatientMedicationsResponse, { skuId?: string } | void>({
      query: (args) => {
        if (!args?.skuId) return apiEndpoints.patientMedications.renewable

        const query = new URLSearchParams({ sku_id: args.skuId })
        return `${apiEndpoints.patientMedications.renewable}?${query}`
      },
      providesTags: ['Prescription'],
    }),
    getPrescriptionByCareActionId: builder.query<PrescriptionOrderLookup, string>({
      query: (careActionPrescriptionId) => apiEndpoints.prescriptions.getById(careActionPrescriptionId),
      providesTags: (_result, _error, careActionPrescriptionId) => [
        { type: 'Prescription', id: careActionPrescriptionId },
      ],
    }),
    // Bundles only this prescription's pending, unlocked renewal labs into one
    // self-serve lab order. Invalidates this prescription (so the detail/check-in
    // pages drop the now-scheduled labs) plus the care-journey and dashboard
    // aggregates that also surface the renewal.
    createPrescriptionMedicationCheckInLabBundle: builder.mutation<
      MedicationCheckInLabBundleResponse,
      { prescriptionId: string }
    >({
      query: ({ prescriptionId }) => ({
        url: apiEndpoints.patientMedications.labBundle(prescriptionId),
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, { prescriptionId }) => [
        { type: 'Prescription', id: prescriptionId },
        'CareJourney',
        'Dashboard',
      ],
    }),
  }),
  overrideExisting: false,
})

export const {
  useCreatePrescriptionPharmacyOrderMutation,
  useGetPatientMedicationQuery,
  useGetRenewablePatientMedicationsQuery,
  useGetPrescriptionByCareActionIdQuery,
  useCreatePrescriptionMedicationCheckInLabBundleMutation,
} = prescriptionApi
